import chroma from 'chroma-js'
import React, { ComponentProps, FC, useEffect, useRef } from 'react'
import SimplexNoise from 'simplex-noise'
import { styled } from '../../lib/stitches'
import { Box } from '../core/Components'

const Canvas = styled('canvas', {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0
})

const Container = styled('div', {
  position: 'relative'
})

const Overlay = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  content: ' ',
  backdropFilter: 'blur(20px)',
  background: 'transparent'
})

interface GradientOptions {
  seed: string
  scale: number
  blockSize: number
  colorPalette: string[]
  interpolationMode: 'lab' | 'lrgb' | 'rgb' | 'hsl' | 'lch'
  colorScale?: chroma.Scale
  fps: number
  speed: number
}

const DEFAULT_OPTIONS: GradientOptions = {
  fps: 24,
  seed: (Math.random() * 100).toString(),
  scale: 3,
  blockSize: 20,
  colorPalette: ['#FAFAF6', '#E1E6FA', '#FAFAF6', '#EFF3FF'],
  interpolationMode: 'lab',
  speed: 1 / 4
}

type GradientProps = ComponentProps<typeof Container> & { options?: Partial<GradientOptions> }
const Gradient: FC<GradientProps> = ({ options, children, ...props }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const _options = { ...DEFAULT_OPTIONS, ...options }
  const { colorPalette, colorScale, ...deps } = _options
  const requestRef = React.useRef<number>()
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const scale = colorScale || chroma.scale(_options.colorPalette).mode('lab').domain([-1, 1])
    const simplex = new SimplexNoise(_options.seed)
    const width = canvas.offsetWidth
    const height = canvas.offsetHeight
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    const { blockSize, fps, speed } = _options
    const fpsInterval = 1000 / fps
    let then = Date.now()
    const redraw = () => {
      if (!canvas) return
      requestRef.current = requestAnimationFrame(redraw)
      let now = Date.now()
      let elapsed = now - then
      if (elapsed > fpsInterval) {
        then = now - (elapsed % fpsInterval)
        const xSize = Math.ceil(width / blockSize)
        const ySize = Math.ceil(height / blockSize)
        for (let x = 0; x < xSize; x++) {
          for (let y = 0; y < ySize; y++) {
            const divider = blockSize * _options.scale
            const n = simplex.noise3D(x / divider, y / divider, Math.sin(now / (1000 / speed)))
            context.save()
            context.fillStyle = scale(n) as unknown as string
            context.beginPath()
            context.fillRect(x * blockSize, y * blockSize, blockSize, blockSize)
            context.restore()
          }
        }
      }
    }
    requestRef.current = requestAnimationFrame(redraw)
    return () => cancelAnimationFrame(requestRef.current)
  }, [...Object.values(deps), JSON.stringify(colorPalette), canvasRef.current])
  return (
    <Container {...props}>
      <Canvas ref={canvasRef} />
      <Overlay />
      <Box css={{ position: 'relative' }}>{children}</Box>
    </Container>
  )
}

export default Gradient
